import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { AddonInputType, Media, UserPlan } from "@/types";
import { AffiliateStatus, UserAffiliateInfo } from "@/types/affiliate";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function toStupidFixed(source: number) {
	// const isDecimal = source % 1 !== 0;
	// return isDecimal ? source.toFixed(2) : source;
	return Math.ceil(source);
}

/** Sanitize storefront keyword search (strip control chars, collapse slashes). */
export function sanitizeSearchPath(input: string | undefined | null): string {
	if (input == null) return "";
	const s = String(input)
		.replace(/[\x00-\x1F\x7F]/g, "")
		.replace(/\/\/+/g, "/")
		.trim()
		.slice(0, 500);
	return s || "";
}

/** Public service search results: `/categories/browse?search=…` */
export function buildBrowseSearchUrl(input: string | undefined | null): string {
	const safe = sanitizeSearchPath(input);
	if (!safe) return "/categories/browse";
	return `/categories/browse?${new URLSearchParams({ search: safe }).toString()}`;
}

/** Safe single path segment for URLs (e.g. category slug). Strips "..", leading slashes, and protocol. */
export function sanitizePathSegment(input: string | undefined | null): string {
	if (input == null) return "";
	const s = String(input)
		.replace(/\.\./g, "")
		.replace(/^[/\\]+/, "")
		.replace(/[\x00-\x1F\x7F]/g, "")
		.trim()
		.slice(0, 200);
	return s || "";
}

const SAFE_QUERY_KEY = /^[a-zA-Z0-9_-]+$/;
const MAX_QUERY_VALUE_LENGTH = 500;
const MAX_QUERY_STRING_LENGTH = 2048;

/** Build a safe query string from URLSearchParams (alphanumeric keys, bounded values/length). */
export function toSafeQueryString(params: URLSearchParams): string {
	const entries: string[] = [];
	for (const [key, value] of params.entries()) {
		if (!SAFE_QUERY_KEY.test(key)) continue;
		const safeValue = String(value).replace(/[\x00-\x1F\x7F]/g, "").slice(0, MAX_QUERY_VALUE_LENGTH);
		entries.push(`${encodeURIComponent(key)}=${encodeURIComponent(safeValue)}`);
	}
	return entries.join("&").slice(0, MAX_QUERY_STRING_LENGTH);
}

export function toHeaderCase(str: string | undefined | null) {
	if (!str) return "";
	return str
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase())
		.join(" ");
}

export function toCompoundDate(source: string | Date) {
	const date = new Date(source);
	const currentDate = new Date();

	const options: Intl.DateTimeFormatOptions = {
		day: "2-digit",
		month: "short",
	};

	if (date.getFullYear() !== currentDate.getFullYear()) {
		options.year = "numeric";
	}

	return date.toLocaleDateString(undefined, options);
}

export function formatRelativeTime(source: string | Date): string {
	const date = new Date(source);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	
	// Calculate time differences
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMs / 3600000);
	const diffDays = Math.floor(diffMs / 86400000);
	const diffWeeks = Math.floor(diffDays / 7);
	const diffYears = Math.floor(diffDays / 365);

	// Format based on time difference
	if (diffSeconds < 60) return `${diffSeconds}s`;
	if (diffMinutes < 60) return `${diffMinutes}m`;
	if (diffHours < 24) return `${diffHours}h`;
	if (diffDays < 7) return `${diffDays}d`;
	if (diffWeeks < 52) return `${diffWeeks}w`;
	// Skip months, go straight to years
	return `${diffYears}y`;
}

export function toMessageDate(source: string | Date) {
	const date = new Date(source);
	const currentDate = new Date();

	const options: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	};

	if (date.getFullYear() !== currentDate.getFullYear()) {
		options.year = "numeric";
	}

	return date.toLocaleDateString(undefined, options);
}

export function formatMessageTime(source: string | Date): string {
	const date = new Date(source);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMs / 3600000);
	const diffDays = Math.floor(diffMs / 86400000);

	if (diffMins < 1) return "Just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < 7) return `${diffDays}d ago`;

	const options: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	};

	if (date.getFullYear() !== now.getFullYear()) {
		options.year = "numeric";
	}

	return date.toLocaleDateString(undefined, options);
}

export function getDateDividerLabel(source: string | Date): string {
	const date = new Date(source);
	const today = new Date();
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	// Reset time to compare dates only
	const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

	if (dateOnly.getTime() === todayOnly.getTime()) {
		return "Today";
	}

	if (dateOnly.getTime() === yesterdayOnly.getTime()) {
		return "Yesterday";
	}

	const options: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
	};

	if (date.getFullYear() !== today.getFullYear()) {
		options.year = "numeric";
	}

	return date.toLocaleDateString(undefined, options);
}

export function isSameDay(date1: string | Date, date2: string | Date): boolean {
	const d1 = new Date(date1);
	const d2 = new Date(date2);
	return (
		d1.getFullYear() === d2.getFullYear() &&
		d1.getMonth() === d2.getMonth() &&
		d1.getDate() === d2.getDate()
	);
}

/** Apple signup with no shared name — backend stores firstName "User", lastName "-". */
export function isAnonymousDisplayName(firstName?: string, lastName?: string) {
	const first = firstName?.trim().toLowerCase() ?? "";
	const last = lastName?.trim() ?? "";
	return first === "user" && last === "-";
}

/** Single letter for avatar fallback (first name). Anonymous Apple → "U". */
export function getInitials(firstName?: string, lastName?: string) {
	if (isAnonymousDisplayName(firstName, lastName)) return "U";

	const first = firstName?.trim() ?? "";
	const last = lastName?.trim() ?? "";
	if (first) return first.charAt(0).toUpperCase();
	if (last) return last.charAt(0).toUpperCase();
	return "N/A";
}

/** Initials avatar: white letter on Techxeed primary teal (#07A8BC). */
export function getAvatarFallbackClassName(
	_firstName?: string,
	_lastName?: string,
	_baseClassName = "bg-secondary-foreground dark:bg-core-midnight dark:text-foreground",
) {
	return "bg-[#07A8BC] text-white";
}

const GENERIC_USER_ERROR_MESSAGE = "Something went wrong. Please try again.";

const SENSITIVE_ERROR_PATTERNS = [
	/\b(AWS|Amazon|SES|SNS|S3|IAM|Cognito|CloudFront)\b/i,
	/\b(Stripe|Google OAuth|OAuth token exchange|checkout\.session|payment_intent)\b/i,
	/\b(us|eu|ap|sa|ca|me|af)-(east|west|south|north|central|northeast|southeast|southwest|northwest)-\d\b/i,
	/\b(region|identity|arn:|request\s?id|message\s?id)\b/i,
	/\b(MongoError|MongooseError|CastError|BSON|ObjectId|E11000|duplicate key)\b/i,
	/\b(JsonWebTokenError|TokenExpiredError|jwt|bearer|authorization)\b/i,
	/\b(secret|private key|access key|api key|credential|client_secret|password hash)\b/i,
	/\b(redirect_uri_mismatch|invalid_grant|invalid_client)\b/i,
	/\b(stack trace|server logs?|localhost|127\.0\.0\.1|0\.0\.0\.0)\b/i,
	/\b(process\.env|NEXT_PUBLIC|ADMIN_IDS|EMAIL_FROM_ADDRESS)\b/i,
	/\/Users\/|\/var\/|\/home\/|\\Users\\/i,
	/\b(cus|pi|cs|ch|sub|price)_[A-Za-z0-9_]{8,}\b/,
	/\b[A-Z0-9_]*(SECRET|TOKEN|PASSWORD|PRIVATE|KEY)[A-Z0-9_]*\b/,
	/\bAKIA[0-9A-Z]{16}\b/,
	/\bghp_[A-Za-z0-9_]{20,}\b/,
	/\bsk-[A-Za-z0-9_-]{20,}\b/,
];

export function sanitizeUserFacingErrorMessage(
	message: unknown,
	fallback = GENERIC_USER_ERROR_MESSAGE,
): string {
	const text = Array.isArray(message)
		? message.map((item) => String(item)).join(", ")
		: typeof message === "string"
			? message
			: message == null
				? ""
				: String(message);

	const trimmed = text.trim();
	if (!trimmed) return fallback;

	return SENSITIVE_ERROR_PATTERNS.some((pattern) => pattern.test(trimmed))
		? fallback
		: trimmed;
}

export function errorIntoString(error: unknown): string {
	if (error === undefined || error === null) return "Unknown error";
	if (typeof error === "string") return sanitizeUserFacingErrorMessage(error);

	const ex = error as any;

	// Handle RTK Query error structure
	if ("data" in ex && ex.data) {
		const data = ex.data;
		// Backend error response format: { message: string } or { error: string, message: string[] }
		if (typeof data === "string") return sanitizeUserFacingErrorMessage(data);
		if (typeof data === "object") {
			if (Array.isArray(data.message)) {
				return sanitizeUserFacingErrorMessage(data.message);
			}
			if (data.message) return sanitizeUserFacingErrorMessage(data.message);
			if (data.error) return sanitizeUserFacingErrorMessage(data.error);
			// Try to stringify the data object if it's complex
			try {
				const stringified = JSON.stringify(data);
				if (stringified !== "{}" && stringified !== "[object Object]") {
					return sanitizeUserFacingErrorMessage(stringified);
				}
			} catch {
				// Ignore JSON stringify errors
			}
		}
	}

	// Handle RTK Query error with status and error fields
	if ("status" in ex) {
		if (typeof ex.status === "number") {
			// HTTP status code
			const statusMessages: Record<number, string> = {
				401: "Authentication required. Please log in again.",
				403: "You don't have permission to access this resource.",
				404: "The requested resource was not found.",
				500: "Server error. Please try again later.",
			};
			if (statusMessages[ex.status]) {
				return statusMessages[ex.status];
			}
			if (ex.data && typeof ex.data === "object") {
				if (ex.data.message) return sanitizeUserFacingErrorMessage(ex.data.message);
				if (ex.data.error) return sanitizeUserFacingErrorMessage(ex.data.error);
			}
		}
	}

	// Handle standard error objects
	if ("message" in ex && typeof ex.message === "string") {
		return sanitizeUserFacingErrorMessage(ex.message);
	}

	// Handle network errors
	if ("error" in ex) {
		if (typeof ex.error === "string") return sanitizeUserFacingErrorMessage(ex.error);
		if (typeof ex.error === "object" && ex.error !== null) {
			// Recursively handle nested error objects
			return errorIntoString(ex.error);
		}
	}

	// Handle RTK Query "FETCH_ERROR" status
	if (ex.status === "FETCH_ERROR" || ex.error === "FETCH_ERROR") {
		return "Network error. Please check your connection and try again.";
	}

	// Handle RTK Query "PARSING_ERROR" status
	if (ex.status === "PARSING_ERROR" || ex.error === "PARSING_ERROR") {
		return "Failed to parse server response. Please try again.";
	}

	// Fallback: try to stringify or return generic message
	try {
		const stringified = JSON.stringify(ex);
		if (stringified !== "{}" && stringified !== "[object Object]") {
			// Clean up the stringified JSON for display
			const displayText = stringified.length > 200 ? stringified.substring(0, 200) + "..." : stringified;
			return sanitizeUserFacingErrorMessage(displayText);
		}
	} catch {
		// Ignore JSON stringify errors
	}

	// Final fallback - try to get any meaningful property
	if (typeof ex === "object" && ex !== null) {
		const keys = Object.keys(ex);
		if (keys.length > 0) {
			// Try to find a meaningful property
			for (const key of ["message", "error", "detail", "description"]) {
				if (key in ex && ex[key] && typeof ex[key] === "string") {
					return sanitizeUserFacingErrorMessage(ex[key]);
				}
			}
		}
	}

	return "An error occurred. Please try again.";
}

export function toLocaleDate(newDate: string) {
	const date = new Date(newDate);
	const formattedDate = date.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});

	return formattedDate;
}

export function deserializeAddonValue(
	addonType: AddonInputType | null,
	value: string | number | boolean,
) {
	let type_ = addonType;

	if (type_ === null) {
		if (typeof value === "number") type_ = AddonInputType.number;
		if (typeof value === "boolean") type_ = AddonInputType.toggle;

		if (typeof value === "string") {
			if (value === "true" || value === "false") type_ = AddonInputType.toggle;
			if (value.match(/^\d*$/) !== null) type_ = AddonInputType.number;
		}

		if (type_ === null) throw new Error("un-deserializable input value");
	}

	switch (type_) {
		case AddonInputType.number:
			return typeof value === "string" ? Number.parseFloat(value) : (value as number);
		case AddonInputType.toggle:
			return typeof value === "string" ? value === "true" : (value as boolean);
	}
}

export function removeUUIDv4Prefix(filename: string) {
	if (typeof filename !== "string") {
		return filename;
	}

	const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}-/i;
	const endSplit = filename.split("/");

	return endSplit[endSplit.length - 1].replace(uuidRegex, "");
}

export function extractFromS3Url(url: string) {
	const s3Prefix = `https://${process.env.NEXT_PUBLIC_AWS_BUCKET_NAME}.s3.${process.env.NEXT_PUBLIC_AWS_REGION}.amazonaws.com/`;
	if (url.startsWith(s3Prefix)) return url.replace(s3Prefix, "");

	return url;
}

export function isUserPremium(plan?: UserPlan) {
	return plan === UserPlan.yearly || plan === UserPlan.monthly;
}

export function isUserAdmin(role: string | undefined) {
	const normalized = role?.trim().toUpperCase();
	return normalized === "ADMIN" || normalized === "DIRECTOR";
}

/** Premium blog articles: staff + paid subscribers; not free/basic or guests. */
export function canViewPremiumBlogArticle(options: {
	isPremium: boolean;
	planDuration?: UserPlan | string | null;
	role?: string | null;
}): boolean {
	if (!options.isPremium) return true;
	if (isUserAdmin(options.role ?? undefined)) return true;
	const duration = options.planDuration;
	if (duration == null) return false;
	return isUserPremium(duration as UserPlan);
}

/**
 * Checks if user is allow to redeem an affiliate payout.
 *
 * For the payout to be eligible, the current date should be one month from the {@link createdAt} and with {@link status} `ELIGIBLE`.
 */
export function isAffiliateEligible(status: AffiliateStatus, createdAt: Date) {
	if (status !== AffiliateStatus.ELIGIBLE) return false;

	const now = new Date();
	return now.getFullYear() > createdAt.getFullYear() || now.getMonth() > createdAt.getMonth();
}

/**
 * Checks if the passed user is allowed to create new affiliates.
 *
 * Currently, false is only returned the user has a {@link UserPlan.free}, and >= 3 affiliates.
 */
export function isAffiliateShareDisabled(plan: UserPlan, affiliateCount: number) {
	// return plan === UserPlan.free && affiliateCount >= 6;
	return false;
}

export function shuffleArray<T>(array: T[]) {
	const shuffled = array.slice();

	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}

	return shuffled;
}

export function isFileVideo(file: File) {
	return file.type.startsWith("video/");
}

export function getPreferredMedia(media: Media[]): Media | null {
	const spotlight = media.find((x) => x.isSpotlight);
	if (spotlight !== undefined) return spotlight;
	return media.at(0) ?? null;
}

export function getThumbnail(thumbnailImage?: string): string | undefined {
	return thumbnailImage !== undefined && thumbnailImage.length > 0 ? thumbnailImage : undefined;
}

export function getPreviewMedia(
	media: Media[],
	thumbnailImage?: string,
	thumbnailDisplayUrl?: string,
): Media | null {
	// Get the spotlight media first (usually first item)
	const spotlight = media.find((x) => x.isSpotlight) || media[0];
	
	if (thumbnailImage !== undefined && thumbnailImage.length > 0) {
		// Check if thumbnail is a YouTube thumbnail URL
		const isYoutubeThumbnail = thumbnailImage.includes('img.youtube.com/vi/');
		
		if (isYoutubeThumbnail) {
			// Extract video ID from YouTube thumbnail URL
			// Format: https://img.youtube.com/vi/VIDEO_ID/maxresdefault.jpg
			const match = thumbnailImage.match(/\/vi\/([^/]+)\//);
			const videoId = match ? match[1] : null;
			
			// Find the media item with this YouTube ID
			const youtubeMedia = media.find((m) => m.youtubeId === videoId);
			
			if (youtubeMedia) {
				// Return the YouTube media with the thumbnail URL
				return {
					...youtubeMedia,
					url: thumbnailImage, // Use the thumbnail URL for display
				};
			}
		}
		
		// Custom thumbnail - use spotlight media's type and source
		return {
			_id: spotlight?._id || "",
			createdAt: spotlight?.createdAt || "",
			updatedAt: spotlight?.updatedAt || "",
			type: spotlight?.type || "IMAGE",
			isSpotlight: false,
			url: thumbnailImage,
			...(thumbnailDisplayUrl?.trim()
				? { displayUrl: thumbnailDisplayUrl.trim() }
				: {}),
			source: spotlight?.source,
			youtubeId: spotlight?.youtubeId,
		};
	}

	if (spotlight !== undefined) return spotlight;

	return media.find((x) => x.type === "IMAGE") ?? null;
}

export function clamp(min: number, max: number, source: number) {
	return Math.min(Math.max(source, min), max);
}

export function getSSOClientId(type: "apple" | "google") {
	switch (type) {
		case "apple":
			return process.env.NEXT_PUBLIC_APPLE_CLIENT_ID!;
		case "google":
			return process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!;
		default:
			throw new Error(`Unsupported SSO type: ${type}`);
	}
}

export function slugify(source: string) {
	return source.toLowerCase().replace(/\s/g, "-");
}

/**
 * Gets the width of the browser's scrollbar in pixels.
 * This is used to prevent layout shift when hiding/showing the scrollbar.
 */
export function getScrollbarWidth(): number {
	// Create a temporary div to measure scrollbar width
	const outer = document.createElement("div");
	outer.style.visibility = "hidden";
	outer.style.overflow = "scroll";
	// @ts-expect-error - msOverflowStyle is a Microsoft-specific property for IE
	outer.style.msOverflowStyle = "scrollbar"; // Needed for IE
	document.body.appendChild(outer);

	// Create an inner div
	const inner = document.createElement("div");
	outer.appendChild(inner);

	// Calculate the scrollbar width
	const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

	// Clean up
	outer.parentNode?.removeChild(outer);

	return scrollbarWidth;
}
